import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CTASection, SectionHeading } from "@/components/sections";
import { Stagger, StaggerItem } from "@/components/primitives";
import { ProductHero } from "@/components/product-parts";
import { Link } from "@/components/LangLink";
import { Check, Info, ImageIcon, ArrowUpRight } from "lucide-react";
import { bendaggiByFamily, BENDAGGI_FAMILIES } from "@/lib/bendaggi";

const PhotoPlaceholder = ({ ratio = "aspect-[4/3]", testid }) => (
  <div className={`relative ${ratio} w-full overflow-hidden rounded-xl bg-cloud border border-[#E7EDF2]`} data-testid={testid}>
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-navy/25">
      <ImageIcon size={26} />
      <span className="text-[10px] font-semibold uppercase tracking-wider">Foto in arrivo</span>
    </div>
  </div>
);

const ProductCard = ({ p }) => (
  <StaggerItem className="h-full">
    <Link
      to={`/prodotti/bendaggi/${p.slug}`}
      data-testid={`bendaggi-card-${p.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7EDF2] bg-white p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-green hover:shadow-[0_28px_56px_-20px_rgba(15,25,35,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
    >
      {p.cover ? (
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-cloud border border-[#E7EDF2]">
          <img src={p.cover} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      ) : (
        <PhotoPlaceholder testid={`bendaggi-card-photo-${p.slug}`} />
      )}
      <div className="mt-4 flex flex-1 flex-col">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-green-deep">{p.tipologia}</span>
        <h3 className="mt-1 font-serif text-xl font-bold text-navy">{p.name}</h3>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Marchio: {p.brand}</p>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.short}</p>
        {p.features?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {p.features.slice(0, 3).map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-navy/70">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green/12 text-green"><Check size={10} strokeWidth={3} /></span>
                {f}
              </li>
            ))}
          </ul>
        )}
        <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-green">
          Scopri il prodotto <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </div>
    </Link>
  </StaggerItem>
);

const FamilySection = ({ f }) => (
  <div id={`fam-${f.id}`} className="scroll-mt-28" data-testid={`bendaggi-family-${f.id}`}>
    <SectionHeading size="sm" maxW="max-w-2xl" eyebrow="Famiglia" title={f.label} intro={f.short} />

    {f.comingSoon ? (
      <div className="mt-6 flex items-center gap-2 rounded-2xl border border-[#E7EDF2] bg-cloud px-5 py-4 text-sm text-navy/60" data-testid={`bendaggi-family-soon-${f.id}`}>
        <Info size={16} className="text-green" /> Prodotti in arrivo. Le schede dettagliate saranno pubblicate con la documentazione tecnica ufficiale.
      </div>
    ) : (
      <Stagger className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {f.products.map((p) => <ProductCard key={p.slug} p={p} />)}
      </Stagger>
    )}
  </div>
);

const Bendaggi = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const families = bendaggiByFamily();
  const raw = searchParams.get("fam") || "tutti";
  const active = BENDAGGI_FAMILIES.some((f) => f.id === raw) ? raw : "tutti";

  const visible = useMemo(
    () => (active === "tutti" ? families : families.filter((f) => f.id === active)),
    [active, families]
  );

  const selectFamily = (id) => {
    if (id === "tutti") setSearchParams({});
    else setSearchParams({ fam: id });
    requestAnimationFrame(() => {
      const el = document.getElementById("bendaggi-famiglie");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const chips = [{ id: "tutti", label: "Tutti i bendaggi" }, ...BENDAGGI_FAMILIES.map((f) => ({ id: f.id, label: f.label }))];

  return (
    <>
      <ProductHero
        theme="navy"
        badge="Terapia compressiva"
        name="Bendaggi"
        layout="split"
        tagline="Bende per la terapia compressiva e il supporto, organizzate in cinque famiglie a seconda della fase di trattamento. Selezioniamo marchi di qualità come Wero Swiss."
        testid="bendaggi-hero"
      />

      <section className="py-16 lg:py-24 bg-white" data-testid="bendaggi-catalogo">
        <div className="container-x">
          <SectionHeading
            size="cat"
            eyebrow="Le tipologie"
            title="Cinque famiglie di bendaggi."
            intro="Ogni tipologia risponde a una fase o a un bisogno specifico del trattamento. Scegli una famiglia per vedere i prodotti disponibili e apri la scheda dedicata. La scelta va sempre guidata dallo specialista."
          />

          {/* Filtri famiglia */}
          <div className="mt-8 flex flex-wrap gap-2.5" role="group" aria-label="Filtra per famiglia" data-testid="bendaggi-filters">
            {chips.map((c) => {
              const on = c.id === active;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectFamily(c.id)}
                  aria-pressed={on}
                  data-testid={`bendaggi-filter-${c.id}`}
                  className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2 ${
                    on ? "border-green bg-green text-white" : "border-[#E7EDF2] text-navy/70 hover:border-green hover:text-green"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Sezioni famiglie */}
          <div id="bendaggi-famiglie" className="mt-12 space-y-16 scroll-mt-28">
            {visible.map((f) => <FamilySection key={f.id} f={f} />)}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Bendaggi;
